import {
  completeGoogleRedirectIfNeeded,
  initFirebase,
  loginWithGoogle,
  logout as firebaseLogout,
  shouldPreferFirebaseRedirect,
  toFirebaseUserSnapshot,
  watchFirebaseAuth
} from '@/helpers/firebaseAuth'
import { firebaseLog, firebaseError, serializeAuthError } from '@/helpers/firebaseDebug'
import isNativeDevice from '@/helpers/isNativeDevie'
import axios from '@/axios/axios-api'

let _unsub = null
let _authDebounce = null
let _pendingAuth = null

export default {
  async initAuthListener ({ commit }) {
    commit('setLoading', true)
    // No arrastrar un error viejo de login al abrir la app.
    commit('setAuthRedirectError', null)
    commit('setAuthRedirectDebug', null)

    // Evita ruedita infinita si onAuthStateChanged nunca dispara (WebView).
    const safetyTimer = setTimeout(() => {
      commit('setLoading', false)
    }, 8000)

    const clearLocalSession = () => {
      localStorage.removeItem('firebase_id_token')
      commit('setFirebaseUser', null)
      commit('setFirebaseIdToken', null)
    }

    try {
      firebaseLog('initAuthListener: start')
      const redirectOutcome = await completeGoogleRedirectIfNeeded()
      firebaseLog('initAuthListener: redirectOutcome', {
        hasError: !!(redirectOutcome && redirectOutcome.error),
        soft: !!(redirectOutcome && redirectOutcome.soft),
        errCode: redirectOutcome && redirectOutcome.error && redirectOutcome.error.code,
        hasResult: !!(redirectOutcome && redirectOutcome.result),
        hasResultUser: !!(redirectOutcome && redirectOutcome.result && redirectOutcome.result.user)
      })

      // Fallos al restaurar sesión / redirect: loguear y seguir sin snackbar.
      // El snackbar de error queda solo para login iniciado por el usuario.
      if (redirectOutcome && redirectOutcome.error) {
        firebaseError('initAuthListener: restore/redirect soft error', {
          code: redirectOutcome.error.code,
          message: redirectOutcome.error.message
        })
      } else {
        const { auth } = initFirebase()
        const raw =
          (redirectOutcome && redirectOutcome.result && redirectOutcome.result.user) ||
          auth.currentUser ||
          null
        if (raw) {
          firebaseLog('initAuthListener: sync raw user → store', { uid: raw.uid, email: raw.email })
          try {
            const token = await raw.getIdToken()
            commit('setFirebaseUser', toFirebaseUserSnapshot(raw))
            commit('setFirebaseIdToken', token)
            if (token) localStorage.setItem('firebase_id_token', token)
            else localStorage.removeItem('firebase_id_token')
            firebaseLog('initAuthListener: token guardado', { hasToken: !!token, tokenLen: token ? token.length : 0 })
          } catch (err) {
            firebaseError('initAuthListener: getIdToken soft error', {
              code: err && err.code,
              message: err && err.message
            })
            clearLocalSession()
          }
        } else {
          firebaseLog('initAuthListener: sin usuario tras redirect (result.user y currentUser null)')
        }
      }

      if (!_unsub) {
        const applyFromWatch = (user, token, error) => {
          if (error) {
            // Sesión guardada inválida / corrupt: limpiar en silencio.
            firebaseError('initAuthListener: auth watch soft error', {
              code: error && error.code,
              message: error && error.message
            })
            clearLocalSession()
            clearTimeout(safetyTimer)
            commit('setLoading', false)
            return
          }
          commit('setFirebaseUser', user)
          commit('setFirebaseIdToken', token)
          if (token) localStorage.setItem('firebase_id_token', token)
          else localStorage.removeItem('firebase_id_token')
          clearTimeout(safetyTimer)
          commit('setLoading', false)
        }

        _unsub = watchFirebaseAuth((payload) => {
          if (payload.error) {
            clearTimeout(_authDebounce)
            _authDebounce = null
            applyFromWatch(payload.user, payload.token, payload.error)
            return
          }
          _pendingAuth = payload
          clearTimeout(_authDebounce)
          const delayMs = 50
          _authDebounce = setTimeout(() => {
            _authDebounce = null
            const p = _pendingAuth
            if (p) applyFromWatch(p.user, p.token, p.error)
          }, delayMs)
        })
      } else {
        clearTimeout(safetyTimer)
        commit('setLoading', false)
      }
    } catch (e) {
      firebaseError('initAuthListener: unexpected soft error', {
        message: e && e.message ? e.message : String(e)
      })
      clearLocalSession()
      clearTimeout(safetyTimer)
      commit('setLoading', false)
    }
  },
  async loginGoogle ({ commit }) {
    commit('setLoading', true)
    try {
      commit('setAuthRedirectError', null)
      commit('setAuthRedirectDebug', null)
      const isNative = !!isNativeDevice()
      const preferRedirect = !isNative && shouldPreferFirebaseRedirect()
      firebaseLog('loginGoogle action', { isNative, preferRedirect })
      await loginWithGoogle({ preferRedirect })
    } catch (e) {
      const { code, message } = serializeAuthError(e)
      firebaseError('loginGoogle action: error', { code, message })
      commit('setAuthRedirectError', `${code}: ${message}`)
    } finally {
      commit('setLoading', false)
    }
  },
  async logout ({ commit }) {
    commit('setLoading', true)
    try {
      await firebaseLogout()
      localStorage.removeItem('firebase_id_token')
      commit('setFirebaseUser', null)
      commit('setFirebaseIdToken', null)
    } finally {
      commit('setLoading', false)
    }
  },
  async fetchMe ({ commit, getters }, { clientCode }) {
    if (!clientCode) return null
    if (!getters.isLoggedIn) return null
    try {
      const { data } = await axios.get(`/api/customer/${clientCode}/me`)
      commit('setMe', data)
      return data
    } catch (e) {
      commit('setMe', null)
      return null
    }
  },
  async saveMe ({ commit, getters, state }, { clientCode, payload }) {
    if (!clientCode) return null
    if (!getters.isLoggedIn) return null
    try {
      const body = payload || state.me || {}
      const { data } = await axios.put(`/api/customer/${clientCode}/me`, body)
      commit('setMe', data)
      return data
    } catch (e) {
      return null
    }
  },
  async saveCustomerAddress ({ commit, getters }, { clientCode, payload }) {
    if (!clientCode || !getters.isLoggedIn) return null
    try {
      const { data } = await axios.post(`/api/customer/${clientCode}/addresses`, payload)
      commit('setMe', data)
      return data
    } catch (e) {
      return null
    }
  },
  async updateCustomerAddress ({ commit, getters }, { clientCode, id, payload }) {
    if (!clientCode || !getters.isLoggedIn || !id) return null
    try {
      const { data } = await axios.put(`/api/customer/${clientCode}/addresses/${id}`, payload)
      commit('setMe', data)
      return data
    } catch (e) {
      return null
    }
  },
  async deleteCustomerAddress ({ commit, getters }, { clientCode, id }) {
    if (!clientCode || !getters.isLoggedIn || !id) return null
    try {
      const { data } = await axios.delete(`/api/customer/${clientCode}/addresses/${id}`)
      commit('setMe', data)
      return data
    } catch (e) {
      return null
    }
  },
  async fetchMisPedidos ({ getters }, { clientCode, page = 1, pageSize = 20 }) {
    if (!clientCode || !getters.isLoggedIn) return []
    try {
      const { data } = await axios.get(`/api/customer/${clientCode}/pedidos`, {
        params: { page, pageSize }
      })
      return Array.isArray(data) ? data : []
    } catch (e) {
      return []
    }
  },
  async fetchMiPedido ({ getters }, { clientCode, id }) {
    if (!clientCode || !getters.isLoggedIn || !id) return null
    try {
      const { data } = await axios.get(`/api/customer/${clientCode}/pedidos/${id}`)
      return data
    } catch (e) {
      return null
    }
  }
}
